<template>
  <section class="aesthetic-media" aria-labelledby="aesthetic-media-title">
    <header class="aesthetic-media__header">
      <div>
        <small>DOKUMENTASI VISUAL</small>
        <h3 id="aesthetic-media-title">Perbandingan Before & After</h3>
        <p>Gunakan sudut dan pencahayaan yang sama agar progres mudah dibandingkan.</p>
      </div>
      <label>
        Sudut pengambilan
        <select :value="standardizedView" :disabled="!editable" class="form-control" @change="$emit('view-change', $event.target.value)">
          <option value="">Pilih sudut</option>
          <option value="front">Depan</option>
          <option value="left">Kiri</option>
          <option value="right">Kanan</option>
          <option value="detail">Detail</option>
        </select>
      </label>
    </header>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-3 mb-0">{{ errorMessage }}</b-alert>

    <div class="comparison-grid">
      <article v-for="role in roles" :key="role.key" class="photo-column">
        <div class="photo-column__heading">
          <span :class="`phase phase--${role.key}`">{{ role.label }}<b v-if="role.required" aria-label="wajib"> *</b></span>
          <small>{{ role.description }}</small>
        </div>

        <div
          class="photo-dropzone"
          :class="{
            'photo-dropzone--active': dragging === role.key,
            'photo-dropzone--filled': displayPreview(role.key),
            'photo-dropzone--disabled': !editable,
          }"
          @dragenter.prevent="dragging = role.key"
          @dragover.prevent="dragging = role.key"
          @dragleave.prevent="leaveDropzone($event, role.key)"
          @drop.prevent="dropFile($event, role.key)"
          @click="openPicker(role.key)"
          @keydown.enter.prevent="openPicker(role.key)"
          @keydown.space.prevent="openPicker(role.key)"
          :tabindex="editable ? 0 : -1"
          :role="editable ? 'button' : null"
          :aria-label="`${currentItem(role.key) ? 'Ganti' : 'Unggah'} ${role.label}`"
        >
          <input
            :ref="`file-${role.key}`"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            :disabled="!editable"
            @change="selectFile($event, role.key)"
          />
          <img v-if="displayPreview(role.key)" :src="displayPreview(role.key)" :alt="`Preview ${role.label}`" />
          <div v-else class="photo-dropzone__empty">
            <span><i class="fas fa-cloud-upload-alt"></i></span>
            <strong>Ambil foto atau pilih file</strong>
            <small>Tarik & lepas foto ke area ini</small>
            <em>JPG, PNG, WEBP · maks. 25 MB</em>
          </div>
          <div v-if="uploading === role.key" class="photo-dropzone__loading">
            <i class="fas fa-circle-notch fa-spin"></i>
            <strong>Mengunggah foto…</strong>
          </div>
          <div v-else-if="displayPreview(role.key) && editable" class="photo-dropzone__overlay">
            <span><i class="fas fa-camera mr-1"></i>Ganti foto</span>
          </div>
        </div>

        <div v-if="pending[role.key]" class="photo-meta photo-meta--pending">
          <span><i class="fas fa-clock mr-1"></i>{{ recordId ? 'Menunggu unggahan' : 'Menyiapkan draft catatan' }}</span>
          <strong>{{ pending[role.key].file.name }}</strong>
        </div>
        <div v-else-if="currentItem(role.key)" class="photo-meta">
          <span><i class="fas fa-check-circle mr-1"></i>Tersimpan privat</span>
          <strong>{{ currentItem(role.key).original_name }}</strong>
          <small>{{ dateTime(currentItem(role.key).captured_at) }} · {{ currentItem(role.key).file_size_human }}</small>
          <div class="photo-actions">
            <button type="button" @click.stop="openContent(currentItem(role.key))"><i class="fas fa-expand-alt mr-1"></i>Lihat penuh</button>
            <button v-if="editable" type="button" class="danger" @click.stop="voidItem(currentItem(role.key))"><i class="fas fa-ban mr-1"></i>Batalkan</button>
          </div>
        </div>
        <div v-else-if="!editable" class="photo-meta photo-meta--empty">Belum ada foto {{ role.label.toLowerCase() }}.</div>
      </article>
    </div>

    <footer>
      <i class="fas fa-shield-alt"></i>
      <span>Foto Before wajib sebelum finalisasi. Foto disimpan privat, memiliki checksum dan jejak audit.</span>
    </footer>
  </section>
</template>

<script>
import ApiService from "@/core/services/api.service.js";

export default {
  name: "AestheticBeforeAfterMedia",
  props: {
    recordId: { type: [Number, String], default: null },
    editable: { type: Boolean, default: false },
    standardizedView: { type: String, default: "" },
  },
  data() {
    return {
      roles: [
        { key: "before", label: "Foto Before", description: "Sebelum treatment", required: true },
        { key: "after", label: "Foto After", description: "Sesudah treatment" },
      ],
      items: [],
      previews: {},
      pending: {},
      dragging: null,
      uploading: null,
      errorMessage: "",
    };
  },
  watch: {
    recordId: {
      immediate: true,
      async handler(value) {
        if (!value) return;
        await this.load();
        const pendingRole = Object.keys(this.pending)[0];
        if (pendingRole) await this.uploadPending(pendingRole);
      },
    },
  },
  beforeDestroy() {
    this.revokePreviews();
    Object.keys(this.pending).forEach((role) => URL.revokeObjectURL(this.pending[role].preview));
  },
  methods: {
    currentItem(role) {
      return this.items.find((item) => item.media_role === role) || null;
    },
    displayPreview(role) {
      return (this.pending[role] && this.pending[role].preview) || (this.currentItem(role) && this.previews[this.currentItem(role).id]) || "";
    },
    openPicker(role) {
      if (!this.editable || this.uploading) return;
      const input = this.$refs[`file-${role}`];
      const element = Array.isArray(input) ? input[0] : input;
      if (element) element.click();
    },
    selectFile(event, role) {
      const file = event.target.files && event.target.files[0];
      if (event.target) event.target.value = "";
      this.prepareFile(file, role);
    },
    dropFile(event, role) {
      this.dragging = null;
      if (!this.editable || this.uploading) return;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      this.prepareFile(file, role);
    },
    leaveDropzone(event, role) {
      if (!event.currentTarget.contains(event.relatedTarget) && this.dragging === role) this.dragging = null;
    },
    async prepareFile(file, role) {
      this.errorMessage = "";
      if (!file) return;
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        this.errorMessage = "Format foto harus JPG, PNG, atau WEBP.";
        return;
      }
      if (file.size > 26214400) {
        this.errorMessage = "Ukuran foto maksimal 25 MB.";
        return;
      }
      if (this.pending[role]) URL.revokeObjectURL(this.pending[role].preview);
      this.$set(this.pending, role, { file, preview: URL.createObjectURL(file) });
      if (!this.recordId) {
        this.$emit("ensure-record");
        return;
      }
      await this.uploadPending(role);
    },
    async uploadPending(role) {
      const pending = this.pending[role];
      if (!pending || !this.recordId || this.uploading) return;
      this.uploading = role;
      this.errorMessage = "";
      const body = new FormData();
      body.append("file", pending.file);
      body.append("media_role", role);
      body.append("captured_at", this.localDate(new Date()));
      body.append("reason", `Unggah ${role === "before" ? "foto before" : "foto after"} treatment.`);
      if (this.standardizedView) body.append("standardized_view", this.standardizedView);
      const current = this.currentItem(role);
      if (current) body.append("replaces_media_id", current.id);
      try {
        await ApiService.post(`vertical-clinical/records/${this.recordId}/media`, body);
        URL.revokeObjectURL(pending.preview);
        this.$delete(this.pending, role);
        await this.load();
        this.$emit("changed");
        this.$bvToast.toast(`${role === "before" ? "Foto Before" : "Foto After"} tersimpan.`, { title: "Berhasil", variant: "success", solid: true });
      } catch (e) {
        this.errorMessage = this.error(e);
      } finally {
        this.uploading = null;
        const nextRole = Object.keys(this.pending)[0];
        if (nextRole) this.$nextTick(() => this.uploadPending(nextRole));
      }
    },
    async load() {
      if (!this.recordId) return;
      try {
        const response = await ApiService.get(`vertical-clinical/records/${this.recordId}/media`);
        this.items = response.data.data || [];
        await this.loadPreviews();
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async loadPreviews() {
      this.revokePreviews();
      await Promise.all(this.items.map(async (item) => {
        try {
          const response = await ApiService.query(item.content_endpoint, { responseType: "blob" });
          this.$set(this.previews, item.id, URL.createObjectURL(response.data));
        } catch (e) {
          // Metadata remains visible when a preview cannot be loaded.
        }
      }));
    },
    revokePreviews() {
      Object.keys(this.previews).forEach((id) => URL.revokeObjectURL(this.previews[id]));
      this.previews = {};
    },
    async openContent(item) {
      try {
        const response = await ApiService.query(item.content_endpoint, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    async voidItem(item) {
      const reason = window.prompt("Alasan pembatalan foto (jejak audit tetap disimpan):");
      if (!reason || reason.trim().length < 5) return;
      try {
        await ApiService.post(`vertical-clinical/media/${item.id}/void`, { reason: reason.trim() });
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.errorMessage = this.error(e);
      }
    },
    localDate(value) {
      const date = new Date(value);
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    },
    dateTime(value) {
      return value ? this.$clinicDateTime(value) : "-";
    },
    error(e) {
      const data = e && e.response && e.response.data;
      if (data && data.errors) return Object.values(data.errors).flat()[0];
      return (data && (data.message || data.error)) || "Foto belum dapat diproses.";
    },
  },
};
</script>

<style scoped>
.aesthetic-media{grid-column:1/-1;margin-bottom:.25rem;padding:1rem;border:1px solid #cfe5e2;border-radius:16px;background:linear-gradient(145deg,#f8fffe,#fff);color:#183153}.aesthetic-media__header{display:flex;align-items:flex-end;justify-content:space-between;gap:1rem;margin-bottom:1rem}.aesthetic-media__header small{color:var(--skin-primary);font-size:.62rem;font-weight:800;letter-spacing:.1em}.aesthetic-media__header h3{margin:.2rem 0;font-size:1rem}.aesthetic-media__header p{margin:0;color:#64748b;font-size:.72rem}.aesthetic-media__header label{width:190px;margin:0;font-size:.68rem;font-weight:800}.comparison-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}.photo-column{min-width:0;padding:.75rem;border:1px solid #dce7ed;border-radius:13px;background:#fff}.photo-column__heading{display:flex;align-items:center;justify-content:space-between;gap:.6rem;margin-bottom:.6rem}.photo-column__heading small{color:#64748b}.phase{padding:.3rem .55rem;border-radius:99px;font-size:.68rem;font-weight:800}.phase--before{background:#e0f2fe;color:#0369a1}.phase--after{background:#dcfce7;color:#15803d}.photo-dropzone{position:relative;display:grid;place-items:center;overflow:hidden;aspect-ratio:4/3;border:2px dashed #8fd9d1;border-radius:12px;background:#f8fafc;cursor:pointer;transition:border-color .16s,background .16s,transform .16s}.photo-dropzone:focus-visible{outline:3px solid rgba(17,149,143,.22);outline-offset:2px}.photo-dropzone--active{border-color:var(--skin-primary);background:#ecfdf5;transform:scale(.995)}.photo-dropzone--filled{border-style:solid;background:#e8f1f5}.photo-dropzone--disabled{cursor:default}.photo-dropzone>input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none}.photo-dropzone>img{width:100%;height:100%;object-fit:cover}.photo-dropzone__empty{display:flex;align-items:center;flex-direction:column;padding:1rem;text-align:center}.photo-dropzone__empty>span{display:grid;place-items:center;width:48px;height:48px;border-radius:14px;background:var(--skin-soft);color:var(--skin-primary);font-size:1.2rem}.photo-dropzone__empty strong{margin:.65rem 0 .15rem}.photo-dropzone__empty small{color:#52677a}.photo-dropzone__empty em{margin-top:.35rem;color:#94a3b8;font-size:.62rem;font-style:normal}.photo-dropzone__loading{position:absolute;inset:0;display:grid;place-content:center;gap:.5rem;background:rgba(15,76,75,.82);color:#fff;text-align:center}.photo-dropzone__loading i{font-size:1.6rem}.photo-dropzone__overlay{position:absolute;inset:auto 0 0;display:flex;justify-content:center;padding:.65rem;background:linear-gradient(transparent,rgba(15,23,42,.82));color:#fff;font-size:.72rem;font-weight:800}.photo-meta{display:flex;flex-direction:column;gap:.2rem;margin-top:.6rem;min-height:62px;color:#52677a;font-size:.67rem}.photo-meta>span{color:#15803d;font-weight:800}.photo-meta>strong{overflow:hidden;color:#334155;text-overflow:ellipsis;white-space:nowrap}.photo-meta--pending>span{color:#b45309}.photo-meta--empty{justify-content:center;color:#94a3b8}.photo-actions{display:flex;gap:.4rem;margin-top:.3rem}.photo-actions button{padding:.3rem .45rem;border:0;border-radius:7px;background:#ecfdf5;color:var(--skin-primary);font-size:.65rem;font-weight:800}.photo-actions button.danger{background:#fef2f2;color:#dc2626}.aesthetic-media>footer{display:flex;align-items:center;gap:.5rem;margin-top:.8rem;color:#64748b;font-size:.65rem}.aesthetic-media>footer i{color:var(--skin-primary)}@media(max-width:760px){.aesthetic-media__header{align-items:flex-start;flex-direction:column}.aesthetic-media__header label{width:100%}.comparison-grid{grid-template-columns:1fr}}@media(max-width:480px){.aesthetic-media{padding:.75rem}.photo-column{padding:.55rem}.photo-dropzone__empty>span{width:40px;height:40px}}
</style>
